import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Box, Grid } from '@mui/material';

import { Divider, LoadingOverlay, Typography } from '@occc/ui-design-system';
import { Select, TextField } from '@occc-app/design-system';
import { FormLayout } from '@occc-app/routes/CampaignComponents/components/FormLayout';
import { walletFormFields } from '@occc-app/routes/Wallet/components/walletFormFields';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';

import { SurveyStatus } from '@occc/graphql';
import { QuestionTypeAdornment } from './QuestionTypeAdornment';
import { QuestionAnswerOptions } from './QuestionAnswerOptions';
import { validateConfiguration } from './validation';
import {
  GeneralSurveyConfigureFormInitialValues,
  GeneralSurveyConfigureFormProps,
  GeneralSurveyConfigureFormValues,
} from './types';

import { FooterActions } from '../FooterActions';
import { SurveyTabs, surveyTypeOptions } from '../../constants';

export const ConfigureForm = ({
  surveyStatus,
  onSubmit,
  onNextStepClick,
  initialValues,
  onCancelClick,
  loading,
  isEntityExists,
  isUserPermittedToEditSurvey,
}: GeneralSurveyConfigureFormProps) => {
  const shouldTypeAndOptionsBeDisabled = Boolean(
    !isUserPermittedToEditSurvey || (surveyStatus && surveyStatus === SurveyStatus.Active),
  );
  const shouldEverythingBeDisabled = Boolean(
    !isUserPermittedToEditSurvey || (surveyStatus && surveyStatus === SurveyStatus.Expired),
  );

  const renderForm = React.useCallback<
    NonNullable<FormRenderProps<GeneralSurveyConfigureFormValues>['render']>
  >(
    ({ handleSubmit, submitting, values, dirty, invalid }) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const actions = (
        <FooterActions
          onNextStepClick={onNextStepClick}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || invalid}
          isEntityExists={isEntityExists}
          currentTab={SurveyTabs.Configure}
          isUserPermittedToEditSurvey={isUserPermittedToEditSurvey}
        />
      );

      return (
        <form onSubmit={handleSubmit}>
          <FormLayout actions={actions}>
            <Box p={3}>
              <Box mb={3}>
                <Typography variant='subtitle1'>Survey</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField name='name' label='Survey name' margin='normal' />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name='description' label='Description' margin='normal' />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box mt={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle1'>Survey Question</Typography>
                    <TextField name='question' label='Question' margin='normal' />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography paragraph variant='subtitle1'>
                      Survey Type
                    </Typography>

                    <CanEditFormFields
                      isEditable={!shouldTypeAndOptionsBeDisabled && !shouldEverythingBeDisabled}
                    >
                      <Select
                        name='questionType'
                        label='Select type'
                        data={surveyTypeOptions}
                        formControlProps={{ size: 'medium', margin: 'none' }}
                        startAdornment={
                          <QuestionTypeAdornment questionType={values.questionType} />
                        }
                      />
                      <QuestionAnswerOptions questionType={values.questionType} />
                    </CanEditFormFields>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Divider />

            <Box p={3}>
              <walletFormFields.IncentivizeResponses values={values} />
            </Box>
          </FormLayout>
        </form>
      );
    },
    [
      onNextStepClick,
      isEntityExists,
      isUserPermittedToEditSurvey,
      shouldTypeAndOptionsBeDisabled,
      shouldEverythingBeDisabled,
      onCancelClick,
    ],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={!shouldEverythingBeDisabled}>
        <Form<GeneralSurveyConfigureFormValues, GeneralSurveyConfigureFormInitialValues>
          onSubmit={onSubmit}
          render={renderForm}
          initialValues={initialValues}
          validate={validateConfiguration}
          mutators={{ ...arrayMutators }}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
